import type { AreaGuide } from "./types"

export const OXFORD_AREA_GUIDES: AreaGuide[] = [
  {
    slug: "summertown",
    name: "Summertown",
    location: "oxford",
    tagline: "North Oxford's thriving suburban heart",
    description:
      "Summertown is Oxford's most popular suburb, stretching along Banbury Road north of the city centre. The high street has an excellent range of independent shops, restaurants, and cafes, while the residential streets offer generous family homes in quiet, leafy settings.\n\nThe area is home to many university families and professionals, drawn by the combination of top schools, green spaces, and easy access to the city centre and Cotswolds. Property ranges from Victorian villas to 1930s detached homes.",
    avgPrice: "£750,000",
    priceRange: "£300,000 - £3,000,000",
    propertyTypes: ["Victorian villas", "1930s detached houses", "Period semis", "Modern townhouses"],
    transport: {
      nearestStation: "Oxford (2 miles by bus)",
      commuteTime: "15 mins to city centre by bus or bike",
      busRoutes: "2, 7, S4 along Banbury Road",
      parking: "Generally available; most properties have driveways",
    },
    lifestyle: {
      restaurants: "The Deli, The Jericho Tavern (nearby), Gees Restaurant",
      schools: "Cherwell School, Summer Fields, Dragon School",
      greenSpaces: "University Parks (nearby), Cutteslowe Park, Port Meadow",
      shopping: "Summertown high street independents",
    },
    highlights: [
      "One of Oxford's best high streets",
      "Excellent state and private schools",
      "Large family homes with gardens",
      "Easy access to the Cotswolds",
      "Strong academic community",
    ],
    bestFor: ["Families", "Academics", "Professionals"],
    imageUrl: "/images/areas/oxford/summertown-800w.webp",
  },
  {
    slug: "jericho",
    name: "Jericho",
    location: "oxford",
    tagline: "Bohemian charm meets canal-side living",
    description:
      "Jericho is Oxford's most characterful neighbourhood — a former Victorian working-class area transformed into a vibrant quarter of independent shops, cafes, pubs, and the Phoenix Picturehouse. Walton Street is the main artery, and the Oxford Canal towpath runs along the western edge.\n\nThe area is compact, walkable, and full of personality. Victorian terraces and cottages predominate, and competition for property is fierce. Jericho suits professionals, couples, and anyone who wants to live in Oxford's most cosmopolitan neighbourhood.",
    avgPrice: "£550,000",
    priceRange: "£275,000 - £1,500,000",
    propertyTypes: ["Victorian terraces", "Artisan cottages", "Period conversions", "Canal-side homes"],
    transport: {
      nearestStation: "Oxford (15 mins walk)",
      commuteTime: "10 mins walk to city centre",
      busRoutes: "6, 11 to city centre; easy walking distance",
      parking: "Heavily permit-controlled; limited availability",
    },
    lifestyle: {
      restaurants: "The Jericho Tavern, Branca, Jericho Cafe",
      schools: "St Barnabas Primary, Phil & Jim's (SS Philip & James)",
      greenSpaces: "Port Meadow, Oxford Canal towpath, University Parks",
      shopping: "Walton Street independents, Little Clarendon Street",
    },
    highlights: [
      "Walton Street independent shops and restaurants",
      "Oxford Canal towpath walks and cycling",
      "Port Meadow — ancient water meadow",
      "Phoenix Picturehouse cinema",
      "Walkable to city centre and university",
    ],
    bestFor: ["Professionals", "Couples", "Academics"],
    imageUrl: "/images/areas/oxford/jericho-800w.webp",
  },
  {
    slug: "headington",
    name: "Headington",
    location: "oxford",
    tagline: "East Oxford's self-contained suburb",
    description:
      "Headington is a large suburb east of Oxford city centre, effectively a small town in its own right with shops, restaurants, schools, and major hospitals. The area is divided into distinct zones: Headington village (charming), the Quarry (quiet), Headley Way (suburban), and the hospital quarter.\n\nProperty is diverse, ranging from period cottages to 1930s semis and modern estates. Headington is popular with hospital staff, families, and anyone who wants more space and value than central Oxford, with its own strong sense of community.",
    avgPrice: "£500,000",
    priceRange: "£225,000 - £1,200,000",
    propertyTypes: ["1930s semis", "Victorian cottages", "Detached family homes", "Modern estates"],
    transport: {
      nearestStation: "Oxford (bus, 15 mins)",
      commuteTime: "15 mins to city centre by bus",
      busRoutes: "Frequent services to city centre (Headington is a major bus corridor)",
      parking: "Generally available; most homes have driveways",
    },
    lifestyle: {
      restaurants: "The Rusty Bicycle, Black Boy pub, Headington cafes",
      schools: "Headington School, Cheney School, Windmill Primary",
      greenSpaces: "Headington Hill Park, Bury Knowle Park, Shotover Country Park",
      shopping: "Headington high street, London Road shops",
    },
    highlights: [
      "Self-contained suburb with excellent local amenities",
      "Shotover Country Park for walking and wildlife",
      "The famous Headington Shark sculpture",
      "Good value compared to central Oxford",
      "Proximity to John Radcliffe and Churchill hospitals",
    ],
    bestFor: ["Families", "Hospital workers", "First-time buyers"],
    imageUrl: "/images/areas/oxford/headington-800w.webp",
  },
  {
    slug: "north-oxford",
    name: "North Oxford",
    location: "oxford",
    tagline: "Grand Victorian villas in a leafy academic setting",
    description:
      "North Oxford is the city's most prestigious residential area, characterised by large Victorian villas on wide, tree-lined roads between Banbury Road and Woodstock Road. The area was developed for university dons and retains a distinctly academic atmosphere.\n\nProperty is almost exclusively large period houses, many with substantial gardens. North Oxford attracts wealthy buyers, academics, and families who want the grandest homes in the city. The area borders University Parks and is walking distance to the city centre.",
    avgPrice: "£1,200,000",
    priceRange: "£500,000 - £5,000,000",
    propertyTypes: ["Grand Victorian villas", "Double-fronted houses", "Large period properties"],
    transport: {
      nearestStation: "Oxford (1 mile)",
      commuteTime: "15 mins walk or cycle to city centre",
      busRoutes: "2, 7, S4 along Banbury Road and Woodstock Road",
      parking: "Most properties have off-street parking",
    },
    lifestyle: {
      restaurants: "Gees Restaurant, The Old Parsonage, Brown's",
      schools: "Dragon School, Summer Fields, Oxford High School",
      greenSpaces: "University Parks, Port Meadow (nearby)",
      shopping: "Summertown (nearby), Little Clarendon Street",
    },
    highlights: [
      "Oxford's grandest residential houses",
      "Adjacent to University Parks",
      "Historic academic atmosphere",
      "Proximity to top preparatory schools",
      "Walking distance to city centre and colleges",
    ],
    bestFor: ["Academics", "Families", "International buyers"],
    imageUrl: "/images/areas/oxford/north-oxford-800w.webp",
  },
  {
    slug: "east-oxford",
    name: "East Oxford",
    location: "oxford",
    tagline: "Diverse, vibrant, and full of energy",
    description:
      "East Oxford — often called 'Cowley Road' after its famous high street — is Oxford's most diverse and energetic neighbourhood. The area is a melting pot of cultures, reflected in its international restaurants, independent shops, and lively atmosphere. The annual Cowley Road Carnival is one of Oxford's biggest events.\n\nProperty is predominantly Victorian terraces, offering the best value for central living in Oxford. The area attracts students, young professionals, and families who want character, diversity, and a strong sense of community.",
    avgPrice: "£400,000",
    priceRange: "£200,000 - £800,000",
    propertyTypes: ["Victorian terraces", "Period conversions", "Edwardian houses"],
    transport: {
      nearestStation: "Oxford (15 mins by bus or bike)",
      commuteTime: "10 mins cycle to city centre",
      busRoutes: "1, 5, 10 along Cowley Road",
      parking: "Permit zones; limited on-street",
    },
    lifestyle: {
      restaurants: "Oli's Thai, Kazbar, Atomic Burger, Magdalen Arms",
      schools: "SS Mary & John Primary, East Oxford Primary",
      greenSpaces: "South Park (panoramic views), Florence Park",
      shopping: "Cowley Road independents, international grocery shops",
    },
    highlights: [
      "Cowley Road — Oxford's most vibrant high street",
      "Annual Cowley Road Carnival",
      "International food scene",
      "South Park panoramic views over Oxford",
      "Best value for central Oxford living",
    ],
    bestFor: ["First-time buyers", "Young professionals", "Students"],
    imageUrl: "/images/areas/oxford/east-oxford-800w.webp",
  },
  {
    slug: "wolvercote",
    name: "Wolvercote",
    location: "oxford",
    tagline: "Village living on Oxford's northern edge",
    description:
      "Wolvercote is a village on the northern edge of Oxford, retaining a rural character despite being within the city boundary. The area borders Port Meadow — an ancient flood meadow that has never been ploughed — and the Oxford Canal, providing exceptional walking and wildlife.\n\nProperty is a mix of period cottages, 1950s semis, and newer family homes. Wolvercote suits buyers who want village living with proximity to Summertown's shops and the city centre, plus easy access to the Cotswolds via the A44.",
    avgPrice: "£550,000",
    priceRange: "£275,000 - £1,500,000",
    propertyTypes: ["Period cottages", "1950s semis", "Detached family homes", "New builds"],
    transport: {
      nearestStation: "Oxford Parkway (2 miles)",
      commuteTime: "20 mins to city centre by bus or bike",
      busRoutes: "6, 6A to city centre via Summertown",
      parking: "Generally available; most homes have off-street parking",
    },
    lifestyle: {
      restaurants: "The Plough, The Trout Inn, Jacob's Inn",
      schools: "Wolvercote Primary, St Frideswide's Primary",
      greenSpaces: "Port Meadow, Wolvercote Common, Oxford Canal",
      shopping: "Wolvercote village shops; Summertown nearby",
    },
    highlights: [
      "Port Meadow — Oxford's ancient water meadow",
      "The Trout Inn — one of Oxford's most famous pubs",
      "Village atmosphere within the city",
      "Oxford Canal towpath walks",
      "Easy access to the Cotswolds",
    ],
    bestFor: ["Families", "Nature lovers", "Professionals"],
    imageUrl: "/images/areas/oxford/wolvercote-800w.webp",
  },
]
